import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { QuestionPreviewComponent } from '../question-preview/question-preview.component';
import { QuestionTypeComponent } from '../shared/question-type/question-type.component';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { QuizQuestionType } from '@edtech-ui/constants/quiz-question-type.enum';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { DifficultyLevelQuiz } from '@edtech-ui/constants/quiz-type.enum';
import { AiQuizOption, AiQuizQuestion } from '@edtech-ui/core/models/quiz-creation/ai-quiz-generator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ToastrService } from 'ngx-toastr';
import { catchError, delay, finalize, of, Subscription, tap } from 'rxjs';
import { CustomValidators } from '@edtech-ui/core/utils/ai-search-validators';
import { textToBlocks } from '@edtech-ui/core/utils/question-blocks.utils';
import { duplicateOptionsValidator } from '@edtech-ui/core/utils/duplicate-option-validators';
import { ActivatedRoute } from '@angular/router';
import { ManualQuizProgressDialogBoxComponent } from '../manual-quiz-progress-dialog-box/manual-quiz-progress-dialog-box.component';
import { MatDialog } from '@angular/material/dialog';
import { AIQuizLimitExceededType } from '@edtech-ui/constants/quiz-limit-type.enum';
import { QuizTotalAiQuestionLimitDialogComponent } from '../quiz-total-ai-question-limit-dialog/quiz-total-ai-question-limit-dialog.component';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';

@Component({
	selector: 'app-manual-mode',
	standalone: true,
	imports: [CommonModule,
		QuestionPreviewComponent,
		QuestionTypeComponent, QuestionPreviewComponent, ReactiveFormsModule,
		MatProgressSpinnerModule, HasPermissionDirective],
	templateUrl: './manual-mode.component.html',
	styleUrl: './manual-mode.component.scss'
})
export class ManualModeComponent implements OnDestroy, OnInit {

	questionCount: number = 1;
	selectedAnswer: number | null = null;

	difficulty_level: DifficultyLevelQuiz = DifficultyLevelQuiz.Easy;
	selectedQuizType: QuizQuestionType = QuizQuestionType.MCQ;
	QuizQuestionType = QuizQuestionType;

	showPreview: boolean = false;
	subscription = new Subscription();

	classroomId!: number;
	quizId!: string;
	manualQuizForm!: FormGroup;

	manualQuestions: AiQuizQuestion[] = [];
	existingQuestions: AiQuizQuestion[] = [];
	progress: number = 0;
	uploadedImageUrl: string | null = null;
	uploadedImagePayload: string | null = null;
	AIQuizLimitExceededType = AIQuizLimitExceededType;
	@Output() switchMode = new EventEmitter<'manual' | 'ai' | 'course' | 'csv' | 'pdf'>();
	PermissionForStudents = PermissionForStudents;

	constructor(
		private _quizService: QuizManagementService,
		private _toastr: ToastrService,
		private route: ActivatedRoute,
		private dialog: MatDialog,
	) {
		this.manualQuizForm = new FormGroup({
			questionText: new FormControl('', [Validators.required]),
			correctAnswer: new FormControl(null, Validators.required),
			options: new FormArray([], duplicateOptionsValidator),
			tfQuestionText: new FormControl('', [Validators.required]),
			selectedTF: new FormControl(null, Validators.required),
			openQuestionText: new FormControl('', [Validators.required]),
			openKeyAnswer: new FormControl('', Validators.required),
			questionCount: new FormControl(1)
		});
		this.addOption();
		this.addOption();

		this.subscription.add(
			this.manualQuizForm.get('questionCount')?.valueChanges.subscribe(value => {
				this.onQuestionCountChange(value);
			})
		);
	}


	ngOnInit(): void {
		this.route.paramMap.subscribe(params => {
			this.classroomId = +params.get('classroomId')!;
			this.quizId = params.get('quizId')!;
		});

		this.subscription.add(
			this._quizService.saveQuizData.subscribe(data => {
				this.existingQuestions = data?.questions || [];
			})
		);
	}


	get options() {
		return this.manualQuizForm.get('options') as FormArray;
	}


	addOption() {
		this.options.push(new FormGroup({
			text: new FormControl('', [Validators.required, CustomValidators.validQuestionText()])
		}));
	}


	removeOption(index: number) {
		if (this.options.length > 2) {
			this.options.removeAt(index);
		}
	}


	selectAnswer(index: number) {
		this.selectedAnswer = index;
	}


	onQuestionCountChange(value: number) {
		if (value < 0) {
			this.questionCount = 0;
		} else if (value > 5) {
			this.questionCount = 5;
		} else {
			this.questionCount = value;
		}
	}


	submitManualQuestions() {
		this.manualQuizForm.markAllAsTouched();
		this.options.controls.forEach(ctrl => ctrl.markAllAsTouched());

		if (!this.isFormValid) {
			this._toastr.warning('Please ensure all questions and options are correctly filled before submitting.');
			return;
		}

		let questionText = '';
		let correctAnswer: string | boolean | null = null;

		if (this.selectedQuizType === QuizQuestionType.MCQ) {
			const parsedOptions: AiQuizOption[] = this.options.controls.map(control => ({
				text: control.get('text')?.value
			}));

			questionText = this.manualQuizForm.get('questionText')?.value?.trim();
			correctAnswer = this.selectedAnswer !== null ? parsedOptions[this.selectedAnswer]?.text : null;

			const validOptions = parsedOptions.filter(option =>
				typeof option.text === 'string' && option.text.trim() !== '');

			if (!questionText || validOptions.length < 2) {
				this._toastr.warning('Please fill all required MCQ fields.');
				return;
			}

			if (this.selectedAnswer === null || correctAnswer === null) {
				this._toastr.warning('Please select the correct answer.');
				return;
			}

			const newQuestion: AiQuizQuestion = {
				question_type: QuizQuestionType.MCQ,
				question_text: questionText,
				question_blocks: textToBlocks(questionText),
				options: validOptions.map(opt => ({ ...opt, blocks: textToBlocks(opt.text) })),
				correct_answer: correctAnswer,
				difficulty_level: this.difficulty_level,
				marks: this.manualQuizForm.get('questionCount')?.value || 1,
				is_ai: false,
				has_diagram: !!this.uploadedImagePayload || !!this.uploadedImageUrl,
				diagram_url: this.uploadedImageUrl && !this.uploadedImagePayload ? this.uploadedImageUrl : undefined,
				diagram_payload: this.uploadedImagePayload || undefined
			} as any;

			const isDuplicate = this.manualQuestions.some(q =>
				q.question_text.trim().toLowerCase() === newQuestion.question_text.trim().toLowerCase() &&
				q.question_type === newQuestion.question_type
			);

			if (isDuplicate) {
				this._toastr.warning('This question already exists!');
				return;
			}
			this.savedQuestions([newQuestion]);

		} else if (this.selectedQuizType === QuizQuestionType.TRUE_FALSE) {
			const tfQuestionControl = this.manualQuizForm.get('tfQuestionText');
			const tfAnswerControl = this.manualQuizForm.get('selectedTF');

			const questionText = tfQuestionControl?.value?.trim();
			const selectedTF = tfAnswerControl?.value;


			if (!questionText || selectedTF === null || selectedTF === undefined) {
				tfQuestionControl?.markAsTouched();
				tfAnswerControl?.markAsTouched();
				this._toastr.warning('Please fill all required True/False fields.');
				return;
			}

			const trueFalseQuestion: AiQuizQuestion = {
				question_type: QuizQuestionType.TRUE_FALSE,
				question_text: questionText,
				question_blocks: textToBlocks(questionText),
				options: [
					{ text: 'true', blocks: textToBlocks('true') },
					{ text: 'false', blocks: textToBlocks('false') }
				],
				correct_answer: String(selectedTF).toLowerCase(),
				difficulty_level: this.difficulty_level,
				marks: this.questionCount,
				is_ai: false,
				has_diagram: !!this.uploadedImagePayload || !!this.uploadedImageUrl,
				diagram_url: this.uploadedImageUrl && !this.uploadedImagePayload ? this.uploadedImageUrl : undefined,
				diagram_payload: this.uploadedImagePayload || undefined
			} as any;

			if (
				this.isDuplicateQuestion(
					trueFalseQuestion.question_text,
					trueFalseQuestion.question_type
				)
			) {
				this._toastr.warning('This question already exists!');
				return;
			}

			this.savedQuestions([trueFalseQuestion]);

		} else if (this.selectedQuizType === QuizQuestionType.OPEN_ENDED) {
			questionText = this.manualQuizForm.get('openQuestionText')?.value?.trim();
			correctAnswer = this.manualQuizForm.get('openKeyAnswer')?.value?.trim();

			if (!questionText || !correctAnswer) {
				this._toastr.warning('Please fill all required Open-Ended fields.');
				return;
			}

			const openEndedQuestion: AiQuizQuestion = {
				question_type: QuizQuestionType.OPEN_ENDED,
				question_text: questionText,
				question_blocks: textToBlocks(questionText),
				options: [],
				correct_answer: correctAnswer,
				difficulty_level: this.difficulty_level,
				marks: this.questionCount,
				is_ai: false,
				has_diagram: !!this.uploadedImagePayload || !!this.uploadedImageUrl,
				diagram_url: this.uploadedImageUrl && !this.uploadedImagePayload ? this.uploadedImageUrl : undefined,
				diagram_payload: this.uploadedImagePayload || undefined
			} as any;

			if (
				this.isDuplicateQuestion(
					openEndedQuestion.question_text,
					openEndedQuestion.question_type
				)
			) {
				this._toastr.warning('This question already exists!');
				return;
			}
			this.savedQuestions([openEndedQuestion]);
		}

		this.showPreview = true;
		this.resetForm();
	}


	private resetForm() {
		this.manualQuizForm.reset();
		this.options.clear();
		this.addOption();
		this.addOption();
		this.questionCount = 1;
		this.selectedAnswer = null;
		this.uploadedImageUrl = null;
		this.uploadedImagePayload = null;
	}

	onImageUpload(event: any) {
		const file = event.target.files[0];
		if (!file) return;

		const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
		if (!validTypes.includes(file.type)) {
			this._toastr.error('Only JPG, JPEG, and PNG formats are allowed.');
			return;
		}

		if (file.size > 5 * 1024 * 1024) {
			this._toastr.error('File size exceeds 5MB limit.');
			return;
		}

		const reader = new FileReader();
		reader.onload = (e: any) => {
			this.uploadedImageUrl = e.target.result;
			this.uploadedImagePayload = e.target.result;
		};
		reader.readAsDataURL(file);
	}

	removeImage() {
		this.uploadedImageUrl = null;
		this.uploadedImagePayload = null;
	}


	get isFormValid(): boolean {
		if (this.selectedQuizType === QuizQuestionType.MCQ) {
			const questionTextValid = this.manualQuizForm.get('questionText')?.valid ?? false;
			const hasDuplicate = this.manualQuizForm.get('options')?.errors?.['duplicateOptions'] ?? false;
			const optionsCountValid = this.options.length >= 2;
			const selectedAnswerValid = this.selectedAnswer !== null;

			return questionTextValid && optionsCountValid && !hasDuplicate && selectedAnswerValid;
		} else if (this.selectedQuizType === QuizQuestionType.TRUE_FALSE) {
			return (this.manualQuizForm.get('tfQuestionText')?.valid ?? false) && (this.manualQuizForm.get('selectedTF')?.valid ?? false);
		} else if (this.selectedQuizType === QuizQuestionType.OPEN_ENDED) {
			return (this.manualQuizForm.get('openQuestionText')?.valid ?? false) && (this.manualQuizForm.get('openKeyAnswer')?.valid ?? false);
		}
		return false;
	}


	savedQuestions(questionsToAdd: AiQuizQuestion[]) {
		this.progress = 0;
		const dialogRef = this.openManulQuizProgressDialogBox();
		const interval = setInterval(() => {
			if (this.progress < 90) {
				this.progress += 5;
				dialogRef.componentInstance.progress = this.progress;
			}
		}, 200);

		const addpayload = { questions: questionsToAdd };

		this.subscription.add(
			this._quizService.postAddQuestion(addpayload, String(this.quizId)).pipe(
				tap((response: any) => {
					if (response.Success) {
						this.progress = 100;
						dialogRef.componentInstance.progress = this.progress;
						this.manualQuestions = response.Data.questions;
						this._quizService.saveQuizData.next(response.Data);
						return;
					}
					if (!response) return;
					const currentTotalQuestion = response.Data.current_total_questions ?? 0;
					const maxTotalQuestion = response.Data.max_total_questions ?? 30;

					if (currentTotalQuestion >= maxTotalQuestion) {
						this.openQuizAiLimitDialogBox(this.AIQuizLimitExceededType.TOTAL)
						return;
					}
					if (response.Data.ai_limit_exceeded && !response.Data.total_limit_exceeded) {
						this.submitManualQuestions();
						return;
					}
				}),
				delay(1000),
				finalize(() => {
					clearInterval(interval);
					dialogRef.close();
				}),
				catchError(() => {
					clearInterval(interval);
					this._toastr.error('something went wrong while adding questions');
					return of(null);
				}),
			).subscribe());
	}


	openManulQuizProgressDialogBox() {
		return this.dialog.open(ManualQuizProgressDialogBoxComponent, {
			width: '400px',
			disableClose: true,
			data: { progress: 0 }
		})
	}


	openQuizAiLimitDialogBox(limitType: AIQuizLimitExceededType, message?: string) {
		const dialogRef = this.dialog.open(QuizTotalAiQuestionLimitDialogComponent, {
			width: '450px',
			disableClose: true,
			data: { type: limitType, message },
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result?.goToManual) {
				this.switchMode.emit('manual');
			}
		});
		return dialogRef;
	}

	private normalizeQuestion(text: string): string {
		return (text || '')
			.toLowerCase()
			.replace(/\s+/g, ' ')
			.replace(/[.,!?;:'"`()[\]{}]/g, '')
			.trim();
	}

	private isDuplicateQuestion(
		questionText: string,
		questionType: QuizQuestionType
	): boolean {

		const normalizedQuestion =
			this.normalizeQuestion(questionText);

		return this.existingQuestions.some(q =>
			this.normalizeQuestion(q.question_text) === normalizedQuestion &&
			q.question_type === questionType
		);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}